import { gsap } from 'gsap';
import {
  BlockEntity,
  setBlockEntityViewLayer,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { IEffect } from './IEffect';
import { PositionType } from '../../../lib/defs/types';

export type TravelHitEffectOptions = {
  block: BlockEntity;
  target: PositionType;
  duration: number;
  delay: number;
  customTween?: (
    block: BlockEntity,
    target: PositionType,
    duration: number,
    delay: number,
  ) => gsap.core.Timeline | gsap.core.Tween;
};

/*
    For filling soda bottles with matching colors with interacted group of basic blocks.
*/
export class TravelHitEffect implements IEffect {
  private readonly scene: GameScene;
  private readonly block: BlockEntity;
  private readonly target: PositionType;
  private readonly duration: number;
  private readonly delay: number;
  private readonly customTween?: (
    block: BlockEntity,
    target: PositionType,
    duration: number,
    delay: number,
  ) => gsap.core.Timeline | gsap.core.Tween;

  constructor(scene: GameScene, options: TravelHitEffectOptions) {
    this.scene = scene;
    this.block = options.block;
    this.target = options.target;
    this.duration = options.duration;
    this.delay = options.delay;
    this.customTween = options.customTween;
  }

  public async execute() {
    await this.tweenBlockToTarget(this.block, this.target);
  }

  private async tweenBlockToTarget(block: BlockEntity, target: PositionType) {
    if (this.customTween)
      return this.customTween?.(block, target, this.duration, this.delay);

    const duration = this.duration;
    const enlargeScale = 1.25;
    const targetScale = 0.6;
    const { x, y } = target;

    return gsap
      .timeline({
        delay: this.delay,
        onStart: () => {
          setBlockEntityViewLayer(this.scene, block, 'foreground');
        },
      })
      .to(
        block,
        {
          x,
          duration: duration,
          ease: 'sine.out',
        },
        0,
      )
      .to(
        block,
        {
          y,
          duration: duration,
          ease: 'sine.out',
        },
        0,
      )
      .to(
        block,
        {
          scaleX: enlargeScale,
          scaleY: enlargeScale,
          duration: duration * 0.5,
          ease: 'sine.out',
        },
        0,
      )
      .to(
        block,
        {
          scaleX: targetScale,
          scaleY: targetScale,
          duration: duration * 0.5,
          ease: 'sine.in',
        },
        duration * 0.5,
      );
  }
}
